import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { seatCandidateV2, unseatV2 } from '../api/v2/teams';
import type { BandV2, TeamSwapOptionAPIV2 } from '../types';

/**
 * The team lens on one candidate in a role's list (team_proposal_spec §6).
 *
 * <p>Seat uniqueness is <em>structural</em>, so a candidate held by another seat
 * is shown inert and labelled with the seat holding them — never hidden, because
 * hiding them would conceal the interlock the recruiter most needs to see, and
 * never moved silently, because the cost of the move lands on a seat they are
 * not looking at. Taking them requires asking, and the button says what it
 * empties.
 *
 * <p>Unseating is offered on the seated candidate as a first-class action: a
 * chair left deliberately empty is a different fact from one nothing could fill.
 */

export interface TeamContext {
  /** This candidate holds this seat. */
  seatedHere: boolean;
  /** Another seat holds them — derived by the page from the team, not asked for. */
  heldAt: { ordinal: number; title: string | null } | null;
  /** Coverage/delta annotations for this candidate, if the backend supplied any. */
  option: TeamSwapOptionAPIV2 | null;
  positionOrdinal: number;
  /** Refetch the team and this role's options after a write. */
  onChanged: () => void;
}

interface Props {
  tenderId: string;
  employeeId: string;
  team: TeamContext;
}

const BAND_LABEL: Record<BandV2, string> = {
  COMPELLING: 'Compelling',
  CREDIBLE: 'Credible',
  ARGUABLE: 'Arguable',
  ELIMINATED: 'Dismissed',
};

export default function TeamSeatControls({ tenderId, employeeId, team }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { seatedHere, heldAt, option, positionOrdinal, onChanged } = team;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch {
      setError(t('teamProposal.writeFailed', 'Could not update the team. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // A 409 means the candidate is held elsewhere. Never auto-retry it as a move:
  // the emptying is the cost and it has to be asked for (§6).
  const seat = () =>
    run(async () => {
      const result = await seatCandidateV2(
        tenderId, positionOrdinal, employeeId, false, Boolean(heldAt),
      );
      if (result === 'HELD_ELSEWHERE') {
        setError(t('teamProposal.heldElsewhereHint', 'Already seated elsewhere — use Move here.'));
      } else if (result === 'POSITION_FULL') {
        // The role holds everyone the tender asked it for. Which of them should
        // leave is the recruiter's call, made looking at that seat — not one
        // this control can take for them.
        setError(
          t('teamProposal.positionFull', 'This role is full — unseat someone here first.'),
        );
      } else if (result === 'INELIGIBLE') {
        setError(t('teamProposal.ineligible', 'This candidate cannot hold this seat.'));
      }
    });

  const move = () =>
    run(async () => {
      const result = await seatCandidateV2(
        tenderId, positionOrdinal, employeeId, true, Boolean(heldAt),
      );
      if (result === 'POSITION_FULL') {
        setError(
          t('teamProposal.positionFull', 'This role is full — unseat someone here first.'),
        );
      }
    });

  const unseat = () => run(() => unseatV2(tenderId, positionOrdinal, employeeId));

  const covers = option?.covers ?? [];
  const gains = option?.gains ?? [];
  const losses = option?.losses ?? [];
  const bandFrom = option?.band_from ?? null;
  const bandTo = option?.band_to ?? null;
  const bandDrops =
    !seatedHere && bandFrom && bandTo && BAND_ORDER[bandTo] > BAND_ORDER[bandFrom];

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {/* What this person would carry for the team, and how replaceable it is. */}
      {covers.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mb-2">
          {covers.map((c) => (
            <li
              key={c.criterion_id}
              className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] ${
                c.scarcity <= 2
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-purple-200 bg-purple-50 text-purple-700'
              }`}
              title={
                c.level === 'THIN'
                  ? t('teamProposal.thinHint', 'Answered by a single weak cue.')
                  : undefined
              }
            >
              {t('teamProposal.covers', 'covers')} {c.criterion ?? c.criterion_id}
              {c.scarcity <= 2 && (
                <span className="ml-1 font-semibold">
                  {t('teamProposal.oneOf', '· 1 of {{count}} here', { count: c.scarcity })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* The consequence of the swap, before it is committed. */}
      {(gains.length > 0 || losses.length > 0 || bandDrops) && (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-[11px]">
          {gains.map((g) => (
            <li key={`g-${g.criterion_id}`} className="text-green-700">
              + {t('teamProposal.wouldCover', 'covers')} {g.criterion ?? g.criterion_id}
            </li>
          ))}
          {losses.map((l) => (
            <li key={`l-${l.criterion_id}`} className="text-rose-700">
              − {t('teamProposal.wouldLose', 'loses')} {l.criterion ?? l.criterion_id}
            </li>
          ))}
          {bandDrops && bandFrom && bandTo && (
            <li className="text-amber-700">
              ↓ {BAND_LABEL[bandFrom]} → {BAND_LABEL[bandTo]}
            </li>
          )}
        </ul>
      )}

      <div className="flex items-center gap-2">
        {seatedHere ? (
          <>
            <span className="inline-flex items-center rounded-lg border border-green-300 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800">
              {t('teamProposal.seated', 'Seated')}
            </span>
            <button
              type="button"
              onClick={unseat}
              disabled={busy}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 px-2.5 py-1 text-xs font-medium text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              title={t('teamProposal.unseatHint', 'Leave this seat deliberately empty')}
            >
              {t('teamProposal.unseat', 'Unseat')}
            </button>
          </>
        ) : heldAt ? (
          <>
            <span
              className="inline-flex items-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2.5 py-1 text-xs text-gray-500"
              title={t('teamProposal.uniquenessHint', 'One person, one seat.')}
            >
              {t('teamProposal.seatedAt', 'Seated — {{role}}', {
                role: heldAt.title ?? `#${heldAt.ordinal}`,
              })}
            </span>
            <button
              type="button"
              onClick={move}
              disabled={busy}
              className="inline-flex items-center rounded-lg border border-brand-200 bg-brand-50 hover:bg-brand-100 disabled:opacity-50 px-2.5 py-1 text-xs font-medium text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              title={t('teamProposal.moveHint', 'Empties {{role}}', {
                role: heldAt.title ?? `#${heldAt.ordinal}`,
              })}
            >
              {t('teamProposal.moveHere', 'Move here')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={seat}
            disabled={busy}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 px-2.5 py-1 text-xs font-medium text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {t('teamProposal.seat', 'Seat')}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-[11px] text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const BAND_ORDER: Record<BandV2, number> = {
  COMPELLING: 0,
  CREDIBLE: 1,
  ARGUABLE: 2,
  ELIMINATED: 3,
};
